import React, { type MouseEvent, type PropsWithChildren } from 'react';

import { utilityClasses } from '@repo/ui-icons';
import cn from 'classnames';

import { Tooltip } from '../Tooltip';
import { H6 } from '../Typography';

import styles from './Button.module.css';
import type { BaseButtonProps } from './types';

export const BaseButton = React.forwardRef<
  HTMLButtonElement,
  PropsWithChildren<BaseButtonProps>
>(
  (
    {
      disabled,
      isActive,
      onClick,
      title,
      titleSide,
      isCritical,
      width,
      className,
      dataTestId,
      children,
      // This is used by Radix when Button is wrapped in a Tooltip.Trigger
      ...rest
    },
    ref
  ) => {
    const handleClick = (event: MouseEvent<HTMLButtonElement>) => {
      if (!disabled) {
        onClick?.(event);
      }
    };

    const buttonClassName = cn(
      className,
      utilityClasses.iconEventWrapper,
      styles.button,
      {
        [styles['is-critical']]: isCritical,
      }
    );

    const buttonWithoutTooltip = (
      <button
        {...rest}
        ref={ref}
        aria-pressed={isActive}
        className={buttonClassName}
        data-testid={dataTestId}
        disabled={disabled}
        style={{ width }}
        onClick={handleClick}
      >
        {children}
      </button>
    );

    if (title) {
      return (
        <Tooltip.Root>
          <Tooltip.Trigger asChild>{buttonWithoutTooltip}</Tooltip.Trigger>
          <Tooltip.Content side={titleSide}>
            <H6 as="span" color="brand-on-bg">
              {title}
            </H6>
          </Tooltip.Content>
        </Tooltip.Root>
      );
    }

    return buttonWithoutTooltip;
  }
);

BaseButton.displayName = 'Button';
